import { CloseCircleFilled, DownOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Avatar, Button, Form, Modal, Popconfirm } from 'antd';
import clsx from 'clsx';
import { filter, findIndex, includes, map } from 'lodash';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMedia } from 'react-use';
import {
  BREAKPOINTS,
  DEFAULT_PAGE_SIZE,
  PROJECT_ROLES_LABEL,
  USER_LIST_ACTIONS,
} from '../../common/constants';
import { titleCase } from '../../common/utils';
import CommonCard from '../../components/CommonCard';
import CommonDropdown from '../../components/CommonDropdown';
import CommonTable from '../../components/CommonTable';
import EllipsisText from '../../components/EllipsisText';
import InfiniteScrollHandler from '../../components/InfiniteScrollHandler';
import ReplaceUserModal from './ReplaceUserModal';
import { REPLICATE_USER } from './graphql/Mutations';
import {
  REPLICATE_USER_DROPDOWN_LIST,
  USER_PROJECT_LIST,
} from './graphql/Queries';

const ReplaceReplicateModal = ({
  showModal,
  setShowModal,
  userRecord,
  refetchUserDetails,
  setUserData,
  actionType,
}) => {
  const initialUserFilter = {
    skip: 0,
    limit: 10,
    userId: userRecord?.id,
  };
  const initialPaginationValue = {
    total: 0,
    current: 1,
  };
  const handleCancel = () => {
    if (setUserData) {
      setUserData();
    }
    setShowModal(false);
  };
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [userListData, setUserListData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [scrollFlag, setScrollFlag] = useState(true);
  const [approverData, setApproverData] = useState();
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [requiredStageIds, setRequiredStageIds] = useState([]);
  const [userFilter, setUserFilter] = useState(initialUserFilter);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectionType, setSelectionType] = useState({});
  const [activeRow, setActiveRow] = useState();
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [drawerUserId, setDrawerUserId] = useState();
  const [selectedUserData, setSelectedUserData] = useState();
  const [isTouch, setIsTouch] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [fetchUserData, { loading }] = useLazyQuery(USER_PROJECT_LIST, {
    variables: { filter: userFilter },
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      let data = [...(res?.userProjectList?.data || [])];
      if (selectionType?.type === 'all') {
        map(data, (item) => {
          setSelectedRows((prev) => [...prev, item]);
        });
        data = data.map((item) => ({
          ...item,
          newReplacedUser: selectedUserData?.[0],
        }));
      }
      const pagination = {
        ...paginationProp,
        total: res?.userProjectList?.total,
      };
      setRequiredStageIds(res?.userProjectList?.requiredProjectIds);
      if (scrollFlag) {
        const datalist = [...userListData, ...data];
        setUserListData(datalist);
        setScrollFlag(false);
        if (datalist?.length === res?.userProjectList?.total) {
          setHasMore(false);
        }
      } else {
        const datalist = [...userListData, ...data];
        setUserListData(datalist);
      }
      setPaginationProp(pagination);
    },
    onError() {},
  });

  const [replicateUser, { loading: replicateUserLoading }] = useMutation(
    REPLICATE_USER,
    {
      onError() {},
      onCompleted() {
        setShowModal(false);
        refetchUserDetails();
        if (setUserData) {
          setUserData();
        }
      },
    },
  );

  useEffect(() => {
    fetchUserData({ variables: { filter: userFilter } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddEditUser = () => {
    setShowModal(true);
  };

  const handleTableChange = (pagination, _, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setPaginationProp({ ...paginationProp, ...pagination });
    if (sorter?.column) {
      setUserFilter({
        ...userFilter,
        skip,
        limit: pagination.pageSize,
        sortBy: {
          field: sorter.columnKey,
          order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      });
      fetchUserData({
        variables: {
          filter: {
            ...userFilter,
            skip,
            limit: pagination.pageSize,
            sortBy: {
              field: sorter.columnKey,
              order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
            },
          },
        },
      });
    } else {
      setUserFilter({
        ...userFilter,
        skip,
        limit: pagination?.pageSize,
      });
      fetchUserData({
        variables: {
          filter: {
            ...userFilter,
            skip,
            limit: pagination?.pageSize,
          },
        },
      });
    }
  };

  const getActionButtons = (record) => {
    const isRequired = includes(requiredStageIds, record?.projectId);
    const handleReplace = (e) => {
      e?.stopPropagation?.();
      setApproverData(record);
      handleAddEditUser();
      setShowReplaceModal(true);
    };
    return isDesktopViewport ? (
      <div
        className={clsx(
          'd-flex justify-between align-center readonly-select',
          isRequired &&
            actionType !== USER_LIST_ACTIONS.REPLICATE &&
            !record?.newReplacedUser &&
            'readonly-select-with-glow',
          record?.newReplacedUser && 'readonly-selected-with-glow',
        )}
      >
        <div
          title={
            record?.newReplacedUser?.name ||
            (record?.newReplacedUser?.user?.name
              ? `${record?.newReplacedUser?.user?.name} - ${record?.newReplacedUser?.agency?.name}`
              : '')
          }
          className="ellipsis-text"
        >
          {record?.newReplacedUser?.name ||
            (record?.newReplacedUser?.user?.name
              ? `${record?.newReplacedUser?.user?.name} - ${record?.newReplacedUser?.agency?.name}`
              : 'Select User')}
        </div>
        <DownOutlined />
      </div>
    ) : (
      <Button
        type="text"
        className={clsx(
          'fw-semi-bold',
          isRequired &&
            actionType !== USER_LIST_ACTIONS.REPLICATE &&
            'text-primary',
          ((!isRequired && !record?.newReplacedUser) ||
            actionType === USER_LIST_ACTIONS.REPLICATE) &&
            'text-secondary',
          record?.newReplacedUser && 'replaced-button',
        )}
        onClick={handleReplace}
      >
        {titleCase(actionType)}
      </Button>
    );
  };

  const getUserActivityCount = (record) => {
    return (
      <div className={clsx('user-activity-count', 'd-flex')}>
        <div className="title">
          Checklists :{' '}
          <span className="count">{record?.assignedChecklistCount || 0}</span>
        </div>
        <div className={clsx('title', 'ml-5')}>
          RFI: <span className="count">{record?.assignedRfiCount || 0}</span>
        </div>
        <div className={clsx('title', 'ml-5')}>
          Approval stage:{' '}
          <span className="count">
            {record?.approvalPendingStageCount || 0}
          </span>
        </div>
      </div>
    );
  };

  const defaultColumns = [
    {
      title: '#',
      key: 'id',
      width: '50px',
      render: (text, record, index) => {
        return <div>{userFilter?.skip + index + 1}</div>;
      },
    },
    {
      title: 'PROJECT',
      dataIndex: 'projectName',
      key: 'projectName',
      render: (_, record) => {
        return <div>{record?.project?.name}</div>;
      },
    },
    {
      title: 'ROLE',
      dataIndex: 'projectEqcTypeName',
      key: 'projectEqcTypeName',
      render: (_, record) => {
        return <EllipsisText text={PROJECT_ROLES_LABEL[record?.roles]} />;
      },
    },
    {
      title: 'ASSIGNMENT',
      dataIndex: 'approvers',
      key: 'approver',
      width: '310px',
      render: (_, record) => getUserActivityCount(record),
    },
    {
      dataIndex: 'replacedUser',
      align: 'right',
      editable: true,
      width: '200px',
      render: (action, record) => getActionButtons(record),
    },
  ];

  const columns = defaultColumns?.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
      }),
    };
  });
  const handleRefetch = () => {
    fetchUserData({
      variables: {
        filter: {
          ...userFilter,
          skip: userListData?.length,
          limit: DEFAULT_PAGE_SIZE,
        },
      },
    });
  };

  const handleConfirm = async () => {
    if (actionType === USER_LIST_ACTIONS.REPLICATE) {
      replicateUser({
        variables: {
          data: {
            id: userRecord?.id,
            replicateUsers:
              selectionType?.type === 'all'
                ? undefined
                : map(
                    filter(userListData, (item) => item?.newReplacedUser),
                    (record) => ({
                      projectId: record?.projectId,
                      userAgencyId: record?.newReplacedUser?.id,
                    }),
                  ),
            isSelectedAll: selectionType?.type === 'all',
            userAgencyId:
              selectionType?.type === 'all'
                ? userListData?.[0]?.newReplacedUser?.id
                : undefined,
          },
        },
      });
    }
  };
  const rowSelection = {
    onChange: (selectedRowKeys, selectedRowsData, info) => {
      setSelectedRows(selectedRowsData);
      setSelectionType(
        !selectedRowsData?.length > 0 ? { type: 'single' } : info,
      );
      setDrawerUserId();
      setTimeout(() => {
        setIsTouch(!!selectedRowsData?.length);
      }, 200);
    },
    selectedRowKeys: selectedRows?.map((data) => data?.id),
    getCheckboxProps: (record) => ({
      disabled: record.name === 'Disabled User',
      // Column configuration not to be checked
      name: record.name,
    }),
  };
  const handleScroll = (event) => {
    setIsOpen(false);
    setActiveRow();
    const { target } = event;
    const { scrollTop, scrollHeight, offsetHeight } = target || {};
    const scrolledToBottom = scrollTop + offsetHeight >= scrollHeight - 10;
    if (scrolledToBottom && hasMore && !loading) {
      const obj = {
        variables: {
          filter: {
            ...userFilter,
            skip: userListData?.length,
            limit: 10,
          },
        },
      };
      fetchUserData(obj);
    }
  };
  const EditableContext = createContext(null);
  const EditableRow = ({ index, ...props }) => {
    const [editableForm] = Form.useForm();
    return (
      <Form form={editableForm} component={false}>
        <EditableContext.Provider value={editableForm}>
          <tr {...props} />
        </EditableContext.Provider>
      </Form>
    );
  };

  const EditableCell = ({
    editable,
    children,
    dataIndex,
    record,
    title,
    ...restProps
  }) => {
    const editableForm = useContext(EditableContext);
    const inputRef = useRef(null);

    useEffect(() => {
      if (isOpen) {
        inputRef?.current?.focus();
        editableForm.setFieldsValue({
          [dataIndex]: record?.newReplacedUser?.id,
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const save = async (_, selectedUserRecord) => {
      try {
        const values = await editableForm.validateFields();
        const index = findIndex(userListData, { id: activeRow?.id });
        if (
          selectedUserRecord?.length > 0 ||
          userListData[index]?.newReplacedUser
        ) {
          setSelectedUserData(
            selectedUserRecord?.length > 0
              ? selectedUserRecord
              : [userListData[index]?.newReplacedUser],
          );
        }
        if (values?.replacedUser && selectedUserRecord) {
          const updatedUser = {
            ...userListData[index],
            newReplacedUser:
              selectedUserRecord?.[0] || userListData[index]?.newReplacedUser,
          };
          userListData[index] = updatedUser;
        }
        setActiveRow();
        setIsOpen(false);
      } catch (errInfo) {
        // eslint-disable-next-line no-console
        console.log('Save failed:', errInfo);
      }
    };

    const handleClear = () => {
      const index = findIndex(userListData, { id: activeRow?.id });
      if (index !== -1) {
        const updatedUser = { ...userListData[index] };
        delete updatedUser?.newReplacedUser;
        userListData[index] = updatedUser;
        setUserListData([...userListData]);
      }
      setActiveRow();
      setIsOpen(false);
    };

    let childNode = children;
    if (editable) {
      childNode =
        activeRow?.id === record?.id &&
        (selectedRows?.length > 0
          ? !includes(
              map(selectedRows, (item) => item?.id),
              record?.id,
            )
          : true) ? (
          <Form.Item
            style={{
              margin: 0,
            }}
            name={dataIndex}
          >
            <CommonDropdown
              inputRef={inputRef}
              placeholder="Select User"
              showSearch
              autoFocus
              allowClear
              optionFilterProp="children"
              open={isOpen && activeRow?.id === record?.id}
              onBlur={() => {
                save('', selectedUserData);
              }}
              onClear={handleClear}
              fetchPolicy="network-only"
              query={REPLICATE_USER_DROPDOWN_LIST}
              variables={{
                filter: {
                  replicateUserId: Number(userRecord?.id),
                },
              }}
              responsePath="replicateUserDropdownList.data"
              valuePath="id"
              labelPath={['user.name', 'agency.name']}
              optionKey="user"
              customOptions={selectedUserData}
              getPopupContainer={() =>
                // eslint-disable-next-line no-undef
                document.querySelector('.replace-table-wrapper')
              }
              onSelect={(id, ...rest) => {
                save(id, [{ id: rest?.[0]?.value, name: rest?.[0]?.label }]);
              }}
            />
          </Form.Item>
        ) : (
          <div
            className={clsx(
              'editable-cell-value-wrap',
              selectedRows?.length > 0 &&
                includes(
                  map(selectedRows, (item) => item?.id),
                  record?.id,
                ) &&
                'cursor-not-allowed',
            )}
            onClick={() => {
              if (
                !(
                  selectedRows?.length > 0 &&
                  includes(
                    map(selectedRows, (item) => item?.id),
                    record?.id,
                  )
                )
              ) {
                setIsOpen(true);
                setActiveRow(record);
                const index = findIndex(userListData, { id: record?.id });
                setSelectedUserData(
                  userListData[index]?.newReplacedUser
                    ? [userListData[index]?.newReplacedUser]
                    : selectedUserData,
                );
              }
            }}
          >
            {children}
          </div>
        );
    }
    return <td {...restProps}>{childNode}</td>;
  };
  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const onChange = async (id, selectedUserRecord) => {
    setSelectedUserData(selectedUserRecord);
    setDrawerUserId(id);
    try {
      map(selectedRows, (record) => {
        const index = findIndex(userListData, { id: record?.id });
        if (selectedUserRecord) {
          const updatedUser = {
            ...userListData[index],
            newReplacedUser: selectedUserRecord?.[0],
          };
          userListData[index] = updatedUser;
          setUserListData([...userListData]);
        }
      });
      setActiveRow();
      setIsOpen(false);
    } catch (errInfo) {
      // eslint-disable-next-line no-console
      console.log('Save failed:', errInfo);
    }
  };

  return (
    <div id="deactive-user-modal">
      <Modal
        maskClosable={false}
        centered
        open={showModal}
        onCancel={() => handleCancel()}
        footer={null}
        closable={false}
        getContainer={() =>
          // eslint-disable-next-line no-undef
          document.getElementById('deactive-user-modal')
        }
      >
        <div className="title mb-5">
          Select a user to duplicate <b>{userRecord?.name}</b>'s assignment
        </div>
        {isDesktopViewport ? (
          <div className="replace-table-wrapper">
            <CommonTable
              columns={columns}
              components={components}
              data={userListData || []}
              onChange={handleTableChange}
              rowKey={(obj) => obj?.id}
              rowSelection={{
                ...rowSelection,
              }}
              onScroll={handleScroll}
              scroll={{
                y: 500,
              }}
            />
          </div>
        ) : (
          <InfiniteScrollHandler
            scrollFlag={scrollFlag}
            loading={loading}
            refetchData={handleRefetch}
            setScrollFlag={setScrollFlag}
            hasMore={hasMore}
            dataLength={userListData?.length}
            skeletonRows={columns?.length - 2}
          >
            {map(userListData, (user, index) => {
              return (
                <CommonCard key={user?.id}>
                  <div className="common-card d-flex">
                    <div className="mr-5 fw-medium">{index + 1}.</div>
                    <div>
                      <div className="card-header fw-medium">
                        <span>
                          <EllipsisText text={user?.project?.name} />
                        </span>
                      </div>
                      <div className="card-content text-secondary">
                        <br />
                        <div className="mb-15 d-flex">
                          <span className="fw-medium mr-5">Role:</span>
                          <EllipsisText
                            className="text-secondary"
                            text={PROJECT_ROLES_LABEL[user?.roles]}
                          />
                        </div>
                        <div className="mb-15">
                          <span className="fw-medium mr-5">Assignment:</span>
                          {getUserActivityCount(user)}
                        </div>
                      </div>
                    </div>
                    <span className="d-flex position-absolute user-action-btn">
                      {getActionButtons(user)}
                    </span>
                  </div>
                </CommonCard>
              );
            })}
          </InfiniteScrollHandler>
        )}
        <div className="text-danger mt-20">
          <b>Note : </b>
          All user with System admin role will be converted to Project admin in
          project
        </div>
        <div className="mt-20 mb-20 d-flex justify-between align-center">
          <div>
            {actionType !== USER_LIST_ACTIONS.REPLICATE && (
              <p className="mt-10 fw-medium d-flex align-center">
                Note:
                <Avatar
                  className={clsx('avatar-color', 'background-primary')}
                  size={14}
                />
                Mandatory to replace.
              </p>
            )}
          </div>
          <div className="form-buttons justify-end">
            <Button
              shape="round"
              className="cancel-button"
              onClick={() => handleCancel()}
            >
              Cancel
            </Button>
            <Popconfirm
              title={`Are you sure you want to ${actionType} User?`}
              onConfirm={handleConfirm}
              okText="Yes"
              cancelText="No"
              disabled={
                actionType === USER_LIST_ACTIONS.REPLICATE
                  ? !filter(userListData, (data) => data?.newReplacedUser)
                      ?.length > 0
                  : !requiredStageIds?.every((r) =>
                      includes(
                        map(
                          filter(userListData, (data) => data?.newReplacedUser),
                          (value) => value?.projectId,
                        ),
                        r,
                      ),
                    )
              }
            >
              <Button
                shape="round"
                type="primary"
                className="save-button ml-5"
                htmlType="submit"
                loading={replicateUserLoading}
                disabled={
                  actionType === USER_LIST_ACTIONS.REPLICATE
                    ? !filter(userListData, (data) => data?.newReplacedUser)
                        ?.length > 0
                    : !requiredStageIds?.every((r) =>
                        includes(
                          map(
                            filter(
                              userListData,
                              (data) => data?.newReplacedUser,
                            ),
                            (value) => value?.projectId,
                          ),
                          r,
                        ),
                      )
                }
              >
                {titleCase(actionType)} (
                {filter(userListData, (data) => data?.newReplacedUser)?.length})
              </Button>
            </Popconfirm>
          </div>
        </div>
        {isTouch && (
          <div className="d-flex align-center justify-center">
            {isDesktopViewport && (
              <div
                className={clsx(
                  'action-drawer',
                  selectedRows?.length > 0
                    ? 'fade-in-from-bottom visible'
                    : 'fade-out-to-bottom',
                )}
                onAnimationEnd={(e) => {
                  if (!selectedRows?.length) {
                    e.currentTarget.classList.remove('visible');
                  }
                }}
              >
                <div className="d-flex align-center">
                  <Button
                    shape="round"
                    type="primary"
                    className="mr-10"
                    iconPosition="end"
                    icon={<CloseCircleFilled />}
                    onClick={() => {
                      setTimeout(() => {
                        setIsTouch(false);
                      }, 200);
                      setSelectedRows([]);
                      setSelectionType({});
                    }}
                  >
                    {`${selectionType?.type === 'all' ? 'All' : selectedRows?.length} ${
                      selectedRows?.length > 1 ? 'Projects' : 'Project'
                    } Selected`}
                  </Button>
                  <CommonDropdown
                    placeholder="Select User"
                    showSearch
                    optionFilterProp="children"
                    allowClear
                    query={REPLICATE_USER_DROPDOWN_LIST}
                    variables={{
                      filter: {
                        replicateUserId: Number(userRecord?.id),
                      },
                    }}
                    responsePath="replicateUserDropdownList.data"
                    valuePath="id"
                    labelPath={['user.name', 'agency.name']}
                    optionKey="user"
                    fetchPolicy="network-only"
                    value={drawerUserId}
                    onChange={(id, selectedUserRecord) => {
                      onChange(id, selectedUserRecord);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
      {showReplaceModal && (
        <ReplaceUserModal
          setShowModal={setShowReplaceModal}
          showModal={showReplaceModal}
          approverData={approverData}
          userListData={userListData}
          setUserListData={setUserListData}
          actionType={actionType}
        />
      )}
    </div>
  );
};

export default ReplaceReplicateModal;
